import { supabase } from './supabase';

// API URL versiya prefiksini (/api/v1) MAJBURIY qiladi. Prod'da VITE_API_URL
// ba'zan bare host (https://api.clary.uz) qilib qo'yilgan — u holda barcha
// chaqiruvlar /api/v1'siz 404 bo'lardi. Bu prefiks yo'q bo'lsa avtomatik qo'shadi
// (allaqachon bor bo'lsa ikki marta qo'shmaydi).
const RAW_BASE = (import.meta.env.VITE_API_URL ?? 'http://localhost:4000').replace(/\/+$/, '');
const BASE = /\/api\/v\d+$/.test(RAW_BASE) ? RAW_BASE : `${RAW_BASE}/api/v1`;

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const authHeaders = await getAuthHeaders();
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...(init.headers as Record<string, string> | undefined),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(err.message ?? 'API error');
  }
  return res.json() as Promise<T>;
}

// ── Clinics ────────────────────────────────────────────────────────────────

export interface ClinicPublic {
  id: string;
  slug: string;
  name: string;
  city: string | null;
  region: string | null;
  address: string | null;
  phone: string | null;
  website: string | null;
  logo_url: string | null;
  primary_color: string;
  organization_type: string;
  is_active: boolean;
}

export interface DoctorPublic {
  id: string;
  full_name: string;
  specialization: string | null;
  photo_url: string | null;
  experience_years: number | null;
}

export interface SlotPublic {
  id: string;
  starts_at: string;
  duration_min: number;
  capacity: number;
  booked_count: number;
  doctor_id: string | null;
  price_snapshot_uzs: number | null;
}

export const clinicsApi = {
  search: (params: { city?: string; query?: string; specialty?: string; page?: number }) =>
    apiFetch<{ data: ClinicPublic[]; total: number }>(
      `/patient/clinics?${new URLSearchParams(
        Object.fromEntries(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)]),
        ),
      )}`,
    ),

  detail: (slug: string) =>
    apiFetch<ClinicPublic & { doctors: DoctorPublic[] }>(`/patient/clinics/${slug}`),

  slots: (slug: string, params: { from: string; to: string; doctor_id?: string }) =>
    apiFetch<SlotPublic[]>(
      `/patient/clinics/${slug}/slots?${new URLSearchParams(
        Object.fromEntries(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)]),
        ),
      )}`,
    ),
};

// ── Bookings ───────────────────────────────────────────────────────────────

export interface BookingInput {
  slot_id: string;
  reason?: string;
}

export interface BookingPublic {
  id: string;
  slot_id: string;
  clinic_id: string;
  status: string;
  created_at: string;
  slot: SlotPublic;
  clinic: Pick<ClinicPublic, 'name' | 'slug' | 'logo_url'>;
}

export const bookingsApi = {
  create: (input: BookingInput) =>
    apiFetch<BookingPublic>('/patient/bookings', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  list: () => apiFetch<BookingPublic[]>('/patient/bookings'),

  cancel: (id: string, reason?: string) =>
    apiFetch<BookingPublic>(`/patient/bookings/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
};

// ── Home-nurse ─────────────────────────────────────────────────────────────

export interface NurseTariff {
  id: string;
  clinic_id: string;
  service: string;
  name_i18n: Record<string, string>;
  base_uzs: number;
  per_km_uzs: number;
  urgent_bonus_uzs: number;
  clinic: Pick<ClinicPublic, 'name' | 'slug' | 'logo_url' | 'city'>;
}

export interface NurseRequestInput {
  clinic_id: string;
  tariff_id: string;
  service: string;
  requester_name: string;
  requester_phone: string;
  address: string;
  address_notes?: string;
  preferred_at?: string;
  is_urgent?: boolean;
  notes?: string;
}

export const nurseApi = {
  tariffs: (params?: { city?: string; service?: string }) =>
    apiFetch<NurseTariff[]>(
      `/patient/nurse/tariffs${params ? `?${new URLSearchParams(params as Record<string, string>)}` : ''}`,
    ),

  request: (input: NurseRequestInput) =>
    apiFetch('/patient/nurse/requests', { method: 'POST', body: JSON.stringify(input) }),

  myRequests: () => apiFetch('/patient/nurse/requests/mine'),
};

// ── Queue status ───────────────────────────────────────────────────────────

export interface QueueStatus {
  booking_id: string;
  status: string;
  position: number | null;
  queue_ahead: number;
  estimated_wait_min: number | null;
  slot: SlotPublic;
  clinic: Pick<ClinicPublic, 'name' | 'logo_url'>;
}

export const queueApi = {
  status: (bookingId: string) => apiFetch<QueueStatus>(`/patient/queue/${bookingId}`),
};

// ── Public lab result (QR skaner) ────────────────────────────────────────────

export interface PublicLabResult {
  id: string;
  status: string;
  urgency: string;
  created_at: string;
  completed_at: string | null;
  reported_at: string | null;
  delivered_at: string | null;
  clinical_notes: string | null;
  patient: {
    full_name: string;
    first_name?: string | null;
    last_name?: string | null;
    patronymic?: string | null;
    dob: string | null;
    gender: 'male' | 'female' | 'other' | 'unknown' | null;
  } | null;
  clinic: {
    name: string;
    logo_url: string | null;
    primary_color: string | null;
    phone: string | null;
    address: string | null;
    city: string | null;
    region: string | null;
  } | null;
  items: Array<{
    id: string;
    name_snapshot: string;
    status: string;
    test: {
      name_i18n: Record<string, string>;
      unit: string | null;
      reference_range_male: string | null;
      reference_range_female: string | null;
      reference_range_child: string | null;
    } | null;
    results: Array<{
      value: string;
      unit: string | null;
      is_abnormal: boolean | null;
      is_final: boolean | null;
      flag: string | null;
    }>;
  }>;
}

export const labPublicApi = {
  // Loginsiz — bemor qog'ozdagi QR'ni skaner qilganda. Token = lab_orders.public_token.
  result: (token: string) => apiFetch<PublicLabResult>(`/lab/public-result/${token}`),
};

// ── Public chek (QR skaner) ──────────────────────────────────────────────────

export interface PublicReceiptItem {
  name: string;
  quantity: number;
  unit_price_uzs: number;
  discount_uzs: number;
  final_amount_uzs: number;
}

export interface PublicReceipt {
  id: string;
  occurred_at: string;
  // 'service' — xizmat/dori cheki; 'payment' — mustaqil pul harakati
  // (qarz to'lash, statsionar depozit): xizmat satri yo'q, faqat summa.
  kind: 'service' | 'payment';
  patient_name: string | null;
  doctor_name: string | null;
  payment_method: string | null;
  items: PublicReceiptItem[];
  med_items: PublicReceiptItem[];
  total_uzs: number;
  paid_uzs: number;
  debt_uzs: number;
  // Bemorning klinikadagi jonli umumiy qarzi (shu chekdan tashqari ham).
  patient_debt_uzs: number;
  status: 'paid' | 'partial' | 'debt';
  clinic: {
    name: string;
    logo_url: string | null;
    primary_color: string | null;
    phone: string | null;
    address: string | null;
    city: string | null;
    region: string | null;
  } | null;
}

export const receiptPublicApi = {
  // Loginsiz — bemor chekdagi QR'ni skaner qilganda. Token = transactions.public_token.
  get: (token: string) => apiFetch<PublicReceipt>(`/transactions/public-receipt/${token}`),
};
